// ============================================================================
// styleToCSS — Convert LayoutElementStyle to React.CSSProperties
// Also handles backward compatibility migration from old preset strings
// ============================================================================

import {
  BORDER_RADIUS_PRESET_MAP,
  FONT_WEIGHT_PRESET_MAP,
  MARGIN_PRESET_MAP,
  PADDING_PRESET_MAP,
  SHADOW_PRESET_MAP,
  SYSTEM_FONTS,
  TEXT_SIZE_PRESET_MAP,
} from './styleConstants';
import type { BoxShadowItem, GradientFill, LayoutElementStyle, TextShadowItem } from './styleTypes';

// Google Fonts loading cache
const loadedFonts = new Set<string>();

export function ensureFontLoaded(fontFamily: string | undefined): void {
  if (!fontFamily || SYSTEM_FONTS.includes(fontFamily) || loadedFonts.has(fontFamily)) return;
  loadedFonts.add(fontFamily);
  const link = document.createElement('link');
  link.rel = 'stylesheet';
  link.href = `https://fonts.googleapis.com/css2?family=${encodeURIComponent(fontFamily)}:wght@100;200;300;400;500;600;700;800;900&display=swap`;
  document.head.appendChild(link);
}

/**
 * Migrate old preset-based style to new numeric values.
 * Does NOT mutate the input — returns a new object with converted values.
 */
export function migrateOldStyle(style: LayoutElementStyle): LayoutElementStyle {
  if (!style) return {};
  const migrated = { ...style };

  // Padding preset → per-side px
  if (typeof migrated.padding === 'string' && migrated.padding in PADDING_PRESET_MAP) {
    const px = PADDING_PRESET_MAP[migrated.padding];
    if (migrated.paddingTop === undefined) migrated.paddingTop = px;
    if (migrated.paddingRight === undefined) migrated.paddingRight = px;
    if (migrated.paddingBottom === undefined) migrated.paddingBottom = px;
    if (migrated.paddingLeft === undefined) migrated.paddingLeft = px;
    delete migrated.padding;
  }

  // MarginTop preset → px
  if (typeof migrated.marginTop === 'string' && migrated.marginTop in MARGIN_PRESET_MAP) {
    migrated.marginTop = MARGIN_PRESET_MAP[migrated.marginTop as string];
  }

  // MarginBottom preset → px
  if (typeof migrated.marginBottom === 'string' && migrated.marginBottom in MARGIN_PRESET_MAP) {
    migrated.marginBottom = MARGIN_PRESET_MAP[migrated.marginBottom as string];
  }

  // TextSize preset → fontSize px
  if (typeof migrated.textSize === 'string' && migrated.textSize in TEXT_SIZE_PRESET_MAP) {
    if (migrated.fontSize === undefined) {
      migrated.fontSize = TEXT_SIZE_PRESET_MAP[migrated.textSize];
    }
    delete migrated.textSize;
  }

  // FontWeight preset → numeric
  if (typeof migrated.fontWeight === 'string') {
    const mapped = FONT_WEIGHT_PRESET_MAP[migrated.fontWeight as string];
    if (mapped) migrated.fontWeight = mapped;
  }

  // BorderRadius preset → px
  if (
    typeof migrated.borderRadius === 'string' &&
    migrated.borderRadius in BORDER_RADIUS_PRESET_MAP
  ) {
    migrated.borderRadius = BORDER_RADIUS_PRESET_MAP[migrated.borderRadius as string];
  }

  // Shadow preset → boxShadows array
  if (typeof migrated.shadow === 'string' && migrated.shadow in SHADOW_PRESET_MAP) {
    const preset = SHADOW_PRESET_MAP[migrated.shadow];
    if (preset && (!migrated.boxShadows || migrated.boxShadows.length === 0)) {
      migrated.boxShadows = [preset];
    }
    delete migrated.shadow;
  }

  return migrated;
}

function buildGradient(gradient: GradientFill): string {
  const stops = gradient.stops
    .sort((a, b) => a.position - b.position)
    .map((s) => `${s.color} ${s.position}%`)
    .join(', ');
  if (gradient.type === 'radial') {
    return `radial-gradient(circle, ${stops})`;
  }
  return `linear-gradient(${gradient.angle ?? 180}deg, ${stops})`;
}

function buildBoxShadow(shadows: BoxShadowItem[]): string {
  return shadows
    .map((s) => `${s.inset ? 'inset ' : ''}${s.x}px ${s.y}px ${s.blur}px ${s.spread}px ${s.color}`)
    .join(', ');
}

function buildTextShadow(ts: TextShadowItem): string {
  return `${ts.x}px ${ts.y}px ${ts.blur}px ${ts.color}`;
}

function buildFilter(style: LayoutElementStyle): string | undefined {
  const parts: string[] = [];
  if (style.filterBlur && style.filterBlur > 0) parts.push(`blur(${style.filterBlur}px)`);
  if (style.filterBrightness !== undefined && style.filterBrightness !== 100)
    parts.push(`brightness(${style.filterBrightness}%)`);
  if (style.filterContrast !== undefined && style.filterContrast !== 100)
    parts.push(`contrast(${style.filterContrast}%)`);
  if (style.filterSaturate !== undefined && style.filterSaturate !== 100)
    parts.push(`saturate(${style.filterSaturate}%)`);
  if (style.filterGrayscale && style.filterGrayscale > 0)
    parts.push(`grayscale(${style.filterGrayscale}%)`);
  if (style.filterHueRotate && style.filterHueRotate > 0)
    parts.push(`hue-rotate(${style.filterHueRotate}deg)`);
  return parts.length > 0 ? parts.join(' ') : undefined;
}

/**
 * Convert LayoutElementStyle into React.CSSProperties.
 * This is the single source of truth for rendering styles — used in:
 * - ModuleBuilder canvas (live preview)
 * - ModuleRecordForm
 * - DealForm
 * - DealDetail
 * - FormElementRenderer
 */
export function styleToCSS(rawStyle: LayoutElementStyle | undefined): React.CSSProperties {
  if (!rawStyle) return {};

  const style = migrateOldStyle(rawStyle);
  const css: React.CSSProperties = {};

  // Ensure Google Font is loaded
  ensureFontLoaded(style.fontFamily);

  // 1. Typography
  if (style.fontFamily)
    css.fontFamily = `"${style.fontFamily}", ${style.fontFamily === 'Georgia' || style.fontFamily === 'Playfair Display' || style.fontFamily === 'Merriweather' ? 'serif' : style.fontFamily === 'Fira Code' || style.fontFamily === 'JetBrains Mono' ? 'monospace' : 'sans-serif'}`;
  if (style.fontSize) css.fontSize = `${style.fontSize}px`;
  if (style.fontWeight) css.fontWeight = style.fontWeight;
  if (style.lineHeight) css.lineHeight = style.lineHeight;
  if (style.letterSpacing !== undefined && style.letterSpacing !== 0)
    css.letterSpacing = `${style.letterSpacing}px`;
  if (style.textDecoration && style.textDecoration !== 'none')
    css.textDecoration = style.textDecoration;
  if (style.textTransform && style.textTransform !== 'none')
    css.textTransform = style.textTransform;
  if (style.textAlign) css.textAlign = style.textAlign;
  if (style.textColor) {
    css.color = style.textColor;
    if (style.textOpacity !== undefined && style.textOpacity < 100) {
      css.color = addAlpha(style.textColor, style.textOpacity / 100);
    }
  }

  // 2. Fill
  if (style.gradient && style.gradient.stops?.length >= 2) {
    css.background = buildGradient(style.gradient);
  } else if (style.backgroundColor) {
    let bg = style.backgroundColor;
    if (style.backgroundOpacity !== undefined && style.backgroundOpacity < 100) {
      bg = addAlpha(bg, style.backgroundOpacity / 100);
    }
    css.backgroundColor = bg;
    // Clear any CSS-class-based gradient (e.g. Tailwind bg-gradient-to-r)
    if (!style.backgroundImage) {
      css.backgroundImage = 'none';
    }
  }
  if (style.backgroundImage) {
    css.backgroundImage = `url(${style.backgroundImage})`;
    if (style.backgroundSize) css.backgroundSize = style.backgroundSize;
    if (style.backgroundPosition) css.backgroundPosition = style.backgroundPosition;
    if (style.backgroundRepeat) css.backgroundRepeat = style.backgroundRepeat;
  }

  // 3. Border
  const hasBorderWidth =
    style.borderWidth ||
    style.borderTopWidth ||
    style.borderRightWidth ||
    style.borderBottomWidth ||
    style.borderLeftWidth;
  if (hasBorderWidth) {
    const bs = style.borderStyle || 'solid';
    const bc = style.borderColor || '#E5E7EB';
    if (
      style.borderTopWidth !== undefined ||
      style.borderRightWidth !== undefined ||
      style.borderBottomWidth !== undefined ||
      style.borderLeftWidth !== undefined
    ) {
      if (style.borderTopWidth !== undefined) {
        css.borderTopWidth = `${style.borderTopWidth}px`;
        css.borderTopStyle = bs;
        css.borderTopColor = bc;
      }
      if (style.borderRightWidth !== undefined) {
        css.borderRightWidth = `${style.borderRightWidth}px`;
        css.borderRightStyle = bs;
        css.borderRightColor = bc;
      }
      if (style.borderBottomWidth !== undefined) {
        css.borderBottomWidth = `${style.borderBottomWidth}px`;
        css.borderBottomStyle = bs;
        css.borderBottomColor = bc;
      }
      if (style.borderLeftWidth !== undefined) {
        css.borderLeftWidth = `${style.borderLeftWidth}px`;
        css.borderLeftStyle = bs;
        css.borderLeftColor = bc;
      }
    } else if (style.borderWidth) {
      css.borderWidth = `${style.borderWidth}px`;
      css.borderStyle = bs;
      css.borderColor = bc;
    }
  }
  // Border radius
  if (
    style.borderTopLeftRadius !== undefined ||
    style.borderTopRightRadius !== undefined ||
    style.borderBottomRightRadius !== undefined ||
    style.borderBottomLeftRadius !== undefined
  ) {
    css.borderRadius = `${style.borderTopLeftRadius ?? 0}px ${style.borderTopRightRadius ?? 0}px ${style.borderBottomRightRadius ?? 0}px ${style.borderBottomLeftRadius ?? 0}px`;
  } else if (typeof style.borderRadius === 'number') {
    css.borderRadius = `${style.borderRadius}px`;
  }

  // 4. Effects
  if (style.boxShadows && style.boxShadows.length > 0) {
    css.boxShadow = buildBoxShadow(style.boxShadows);
  }
  if (style.textShadow) {
    css.textShadow = buildTextShadow(style.textShadow);
  }
  if (style.opacity !== undefined && style.opacity < 100) {
    css.opacity = style.opacity / 100;
  }
  if (style.backdropBlur && style.backdropBlur > 0) {
    css.backdropFilter = `blur(${style.backdropBlur}px)`;
  }
  if (style.mixBlendMode && style.mixBlendMode !== 'normal') {
    css.mixBlendMode = style.mixBlendMode;
  }

  // 5. Spacing
  if (style.paddingTop !== undefined) css.paddingTop = `${style.paddingTop}px`;
  if (style.paddingRight !== undefined) css.paddingRight = `${style.paddingRight}px`;
  if (style.paddingBottom !== undefined) css.paddingBottom = `${style.paddingBottom}px`;
  if (style.paddingLeft !== undefined) css.paddingLeft = `${style.paddingLeft}px`;
  if (typeof style.marginTop === 'number') css.marginTop = `${style.marginTop}px`;
  if (style.marginRight !== undefined) css.marginRight = `${style.marginRight}px`;
  if (typeof style.marginBottom === 'number') css.marginBottom = `${style.marginBottom}px`;
  if (style.marginLeft !== undefined) css.marginLeft = `${style.marginLeft}px`;
  if (style.gap !== undefined) css.gap = `${style.gap}px`;

  // 6. Size
  if (style.width) css.width = style.width;
  if (style.minHeight !== undefined) css.minHeight = `${style.minHeight}px`;
  if (style.maxHeight !== undefined) css.maxHeight = `${style.maxHeight}px`;
  if (style.overflow && style.overflow !== 'visible') css.overflow = style.overflow;

  // 7. Entry animation (CSS class applied separately via getAnimationClass)
  // Not part of inline styles — handled by className

  // 8. Filters
  const filterVal = buildFilter(style);
  if (filterVal) css.filter = filterVal;

  return css;
}

/**
 * Get animation CSS class name for an element.
 */
export function getAnimationClass(style: LayoutElementStyle | undefined): string {
  if (!style) return '';
  const migrated = migrateOldStyle(style);
  if (!migrated.entryAnimation || migrated.entryAnimation === 'none') return '';
  return `cp-anim-${migrated.entryAnimation}`;
}

/**
 * Get animation inline style (duration, delay, easing).
 */
export function getAnimationStyle(style: LayoutElementStyle | undefined): React.CSSProperties {
  if (!style) return {};
  const migrated = migrateOldStyle(style);
  if (!migrated.entryAnimation || migrated.entryAnimation === 'none') return {};

  const css: React.CSSProperties = {};
  if (migrated.animationDuration) {
    css.animationDuration = `${migrated.animationDuration}ms`;
  }
  if (migrated.animationDelay) {
    css.animationDelay = `${migrated.animationDelay}ms`;
  }
  if (migrated.animationEasing) {
    css.animationTimingFunction =
      migrated.animationEasing === 'spring'
        ? 'cubic-bezier(0.175, 0.885, 0.32, 1.275)'
        : migrated.animationEasing;
  }
  return css;
}

/**
 * Get hover effect props for framer-motion whileHover.
 */
export function getHoverProps(
  style: LayoutElementStyle | undefined,
): Record<string, unknown> | undefined {
  if (!style?.hoverEffect) return undefined;
  const h = style.hoverEffect;
  const hover: Record<string, unknown> = {};
  if (h.scale) hover.scale = h.scale;
  if (h.translateY) hover.y = h.translateY;
  if (h.brightness) hover.filter = `brightness(${h.brightness}%)`;
  if (h.opacity !== undefined) hover.opacity = h.opacity / 100;
  if (h.backgroundColor) hover.backgroundColor = h.backgroundColor;
  if (h.borderColor) hover.borderColor = h.borderColor;
  if (h.shadow) hover.boxShadow = h.shadow;

  if (Object.keys(hover).length === 0) return undefined;
  return {
    whileHover: hover,
    transition: { duration: (h.transitionDuration || 200) / 1000 },
  };
}

// ============================================================================
// Utility helpers
// ============================================================================

function addAlpha(hex: string, alpha: number): string {
  // If already rgba, replace alpha
  if (hex.startsWith('rgba')) {
    return hex.replace(/,\s*[\d.]+\)/, `, ${alpha})`);
  }
  // Convert hex to rgba
  const r = parseInt(hex.slice(1, 3), 16);
  const g = parseInt(hex.slice(3, 5), 16);
  const b = parseInt(hex.slice(5, 7), 16);
  if (isNaN(r) || isNaN(g) || isNaN(b)) return hex;
  return `rgba(${r}, ${g}, ${b}, ${alpha})`;
}

/**
 * Get icon wrapper style for rendering icons in elements.
 */
export function getIconStyle(style: LayoutElementStyle | undefined): {
  iconStyle: React.CSSProperties;
  wrapperStyle: React.CSSProperties;
} | null {
  if (!style?.icon) return null;

  const iconStyle: React.CSSProperties = {};
  const wrapperStyle: React.CSSProperties = {};

  if (style.iconSize) {
    iconStyle.width = `${style.iconSize}px`;
    iconStyle.height = `${style.iconSize}px`;
  }
  if (style.iconColor) {
    iconStyle.color = style.iconColor;
    if (style.iconOpacity !== undefined && style.iconOpacity < 100) {
      iconStyle.color = addAlpha(style.iconColor, style.iconOpacity / 100);
    }
  }
  if (style.iconRotation) {
    iconStyle.transform = `rotate(${style.iconRotation}deg)`;
  }

  if (style.iconBackgroundColor) {
    wrapperStyle.backgroundColor = style.iconBackgroundColor;
    wrapperStyle.borderRadius =
      style.iconBackgroundRadius !== undefined ? `${style.iconBackgroundRadius}px` : '9999px';
    wrapperStyle.padding = `${style.iconBackgroundPadding ?? 4}px`;
    wrapperStyle.display = 'inline-flex';
    wrapperStyle.alignItems = 'center';
    wrapperStyle.justifyContent = 'center';
  }

  return { iconStyle, wrapperStyle };
}
